import React, { useState, useMemo, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    IconButton, 
    Tooltip, 
    Chip,
    CircularProgress 
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Cookies from 'js-cookie';
import api from '../../../../../../services/api';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RouteIcon from '@mui/icons-material/Route';

export default function TabelaRotas({ darkMode }) {
    const [rotas, setRotas] = useState([]);
    const [loading, setLoading] = useState(false);

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    const getStats = (rota) => {
        if (!rota.pontos || rota.pontos.length === 0) return { distance: "0.00", time: 0 };
        let totalKm = 0;
        let totalExecutionTime = 0;

        const allPoints = [];
        if (rota.ponto_partida) allPoints.push(rota.ponto_partida);
        rota.pontos.forEach(p => {
            allPoints.push(p.instalacao);
            if (p.is_ronda) totalExecutionTime += 15;
        });
        if (rota.ponto_final) allPoints.push(rota.ponto_final);

        allPoints.forEach((p, index) => {
            if (index > 0) {
                const prev = allPoints[index - 1];
                if (p && prev && p.latitude && p.longitude && prev.latitude && prev.longitude) {
                    totalKm += calculateDistance(
                        parseFloat(prev.latitude), parseFloat(prev.longitude),
                        parseFloat(p.latitude), parseFloat(p.longitude)
                    );
                }
            }
        });

        const travelTime = (totalKm / 40) * 60;
        return {
            distance: totalKm.toFixed(2),
            time: Math.round(travelTime + totalExecutionTime)
        };
    };

    const listarRotas = async () => {
        setLoading(true);
        try {
            const headers = { 'Authorization': 'Bearer ' + Cookies.get("token") };
            const response = await api.get("v1/protected/rotarondamotorizada/listartodos", { headers });
            if (response.data) setRotas(response.data);
        } catch (error) { 
            console.error(error); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => {
        listarRotas();
    }, []);

    const handleDeleteRota = async (id) => {
        if (!window.confirm("Deseja realmente excluir esta rota?")) return;
        try {
            const headers = { 'Authorization': 'Bearer ' + Cookies.get("token") };
            await api.delete(`v1/protected/rotarondamotorizada/deletar/${id}`, { headers });
            listarRotas();
        } catch (error) {
            alert("Erro ao excluir rota");
        }
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'id_rota_ronda_motorizada',
            header: 'Ações',
            size: 100,
            Cell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Editar Rota">
                        <IconButton onClick={() => window.open('/gestao/operacional/rondamotorizada/rotas/editar/' + row.original.id_rota_ronda_motorizada, '_blank')}>
                            <EditIcon color="primary" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir Rota">
                        <IconButton color="error" onClick={() => handleDeleteRota(row.original.id_rota_ronda_motorizada)}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        },
        { accessorKey: 'id_rota_ronda_motorizada', header: 'ID', size: 80 },
        { accessorKey: 'nome_rota', header: 'Nome da Rota', size: 250 },
        { 
            accessorKey: 'ponto_partida.nome_fantasia', 
            header: 'Partida', 
            size: 150,
            Cell: ({ row }) => row.original.ponto_partida?.nome_fantasia || row.original.ponto_partida?.nome_empresarial || 'N/A'
        },
        { 
            accessorKey: 'ponto_final.nome_fantasia', 
            header: 'Chegada', 
            size: 150,
            Cell: ({ row }) => row.original.ponto_final?.nome_fantasia || row.original.ponto_final?.nome_empresarial || 'N/A'
        },
        { 
            accessorKey: 'qtd_instalacoes', 
            header: 'Instalações', 
            size: 100,
            Cell: ({ row }) => row.original.pontos?.length || 0
        },
        { 
            header: 'Distância', 
            size: 100,
            Cell: ({ row }) => `${getStats(row.original).distance} KM`
        },
        { 
            header: 'Tempo Est.', 
            size: 100,
            Cell: ({ row }) => `${getStats(row.original).time} min`
        },
        { 
            accessorKey: 'status', 
            header: 'Status', 
            size: 100,
            Cell: () => (
                <Chip 
                    label="ATIVO" 
                    color="success" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }} 
                />
            )
        },
    ], []);

    const tableTheme = createTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
        }
    });

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RouteIcon color="primary" fontSize="large" />
                    <Typography variant="h5" fontWeight="bold">Rotas de Ronda Motorizada</Typography>
                </Box>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={() => window.location.href = '/gestao/operacional/rondamotorizada/rotas/cadastrar'}
                >
                    Nova Rota
                </Button>
            </Box>

            <ThemeProvider theme={tableTheme}>
                <MaterialReactTable
                    columns={columns}
                    data={rotas}
                    localization={MRT_Localization_PT_BR}
                    enableRowActions={false}
                    muiTablePaperProps={{
                        elevation: 2,
                        sx: { borderRadius: 3 }
                    }}
                />
            </ThemeProvider>
        </Box>
    );
}
